import { Component } from 'react';

/**
 * Top-level error boundary. Catches render-time exceptions anywhere in the
 * React tree and replaces the blank-white-screen-of-death with a recovery UI.
 * Also logs the error to the console so we can debug from the user's device.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so it shows up in DevTools / Sentry if installed later
    // eslint-disable-next-line no-console
    console.error('[Kinsy ErrorBoundary]', error, info);
    this.setState({ info });
  }

  handleReset = () => {
    // Best-effort SW + cache clear before reload — fixes blank-page-after-deploy
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      }).catch(() => {});
    }
    if ('caches' in window) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
    }
    setTimeout(() => window.location.reload(), 300);
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#FDFBF7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'Helvetica, Arial, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            background: 'white',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔄</div>
          <h1 style={{ fontSize: 22, color: '#1C1917', margin: '0 0 8px', fontWeight: 600 }}>
            Hmm, something glitched
          </h1>
          <p style={{ fontSize: 15, color: '#57534E', lineHeight: 1.5, margin: '0 0 20px' }}>
            Kinsy hit an unexpected error. This is usually fixed by a quick refresh — we&apos;ll clear
            the cache for you.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              background: '#0D9488',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 999,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Refresh &amp; clear cache
          </button>
          {this.state.error?.message && (
            <details style={{ marginTop: 16, textAlign: 'left' }}>
              <summary style={{ fontSize: 12, color: '#A8A29E', cursor: 'pointer' }}>
                Technical details
              </summary>
              <pre style={{
                fontSize: 11,
                color: '#78716C',
                background: '#F5F5F4',
                padding: 12,
                borderRadius: 8,
                marginTop: 8,
                overflow: 'auto',
                maxHeight: 200,
                whiteSpace: 'pre-wrap',
              }}>
                {String(this.state.error?.message || this.state.error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
